import { Box, Button, Checkbox, Flex, useDisclosure } from '@chakra-ui/react';
import {
  ApprovalCard,
  CloseCircleOutline,
  getError,
  Individual,
  individualFullName,
  PageEmptyState,
  TickCircleOutline,
  useToast,
} from '@aslanpay/portal-common-lib';
import { useCallback, useEffect, useState } from 'react';
import { getPointsValue } from 'app/utils/points';

import BulkActionBar from 'app/components/BulkActionBar/BulkActionBar';
import ApproveRecognitionModal from 'app/components/Modals/RecognitionApprovalsModals/RecognitionApprovalModal/RecognitionApprovalModal';
import RejectRecognitionModal from 'app/components/Modals/RecognitionApprovalsModals/RejectRecognitionModal/RejectRecognitionModal';

import RecognitionDetailsModal from 'app/components/Modals/RecognitionApprovalsModals/RecognitionDetailsModal/RecognitionDetailsModal';

import useRecognition from 'app/hooks/useRecognition';

import { ApprovalsTabEmptyPage } from '../Approvals.function';

import { RecognitionWithPot } from '../Approvals.types';

import { RecognitionApprovalsListProps } from './RecognitionApprovalsList.types';

// TXN-80000 is what wallet-manager returns now; GEN-10409 is kept for backends not yet on that version.
const INSUFFICIENT_BALANCE_CODES = ['TXN-80000', 'GEN-10409'];

const isInsufficient = (item: RecognitionWithPot) =>
  item.groupWithPot?.pot != null && (item.groupWithPot.pot.balance ?? 0) < (item.points ?? 0);

function RecognitionApprovalsList({ list, tab, potDropdown }: RecognitionApprovalsListProps) {
  const toast = useToast();

  const [selected, setSelected] = useState<RecognitionWithPot[]>([]);
  const [pendingItems, setPendingItems] = useState<RecognitionWithPot[]>([]);
  const [selectedRecognition, setSelectedRecognition] = useState<RecognitionWithPot>();

  // Clear selection whenever the visible list changes (tab switch or pot filter change)
  useEffect(() => {
    setSelected([]);
  }, [list]);

  const { isOpen: isApproveOpen, onOpen: openApprove, onClose: closeApprove } = useDisclosure();
  const { isOpen: isRejectOpen, onOpen: openReject, onClose: closeReject } = useDisclosure();

  const { isOpen: isDetailsOpen, onOpen: openDetails, onClose: closeDetails } = useDisclosure();

  const { mutateAsync: approveRecognition } = useRecognition.useApproveRecognition();
  const { mutateAsync: rejectRecognition } = useRecognition.useRejectRecognition();

  const onApprove = useCallback(
    async (items: RecognitionWithPot[]) => {
      const isMultiple = items.length > 1;
      try {
        await Promise.all(items.map((item) => approveRecognition(item.id)));
        toast({
          position: 'top-right',
          status: 'success',
          title: isMultiple
            ? `${items.length} entries successfully approved`
            : 'Recognition approved',
          description: isMultiple
            ? 'The selected requests have been approved'
            : 'The sender has been notified',
        });

        closeApprove();
        setPendingItems([]);
        setSelected([]);
      } catch (error) {
        const isInsufficientBalanceError =
          error instanceof Error && INSUFFICIENT_BALANCE_CODES.includes(getError(error).code);

        if (isInsufficientBalanceError) {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Insufficient balance',
            description:
              'The source pot does not have enough points to cover this recognition. Refresh and try again.',
          });
          return;
        }

        toast({
          position: 'top-right',
          status: 'error',
          title: isMultiple
            ? `Failed to approve ${items.length} entries`
            : 'Failed to approve recognition',
        });
      }
    },
    [approveRecognition, toast, closeApprove]
  );

  const onReject = useCallback(
    async (items: RecognitionWithPot[], reason: string) => {
      const isMultiple = items.length > 1;
      try {
        await Promise.all(
          items.map((item) => rejectRecognition({ recognitionId: item.id, body: { reason } }))
        );
        toast({
          position: 'top-right',
          status: 'success',
          title: isMultiple
            ? `${items.length} entries successfully declined`
            : 'Recognition declined',
          description: isMultiple
            ? 'The selected requests have been declined'
            : 'The sender has been notified',
        });

        closeReject();
        setPendingItems([]);
        setSelected([]);
      } catch {
        toast({
          position: 'top-right',
          status: 'error',
          title: isMultiple
            ? `Failed to decline ${items.length} entries`
            : 'Failed to decline recognition',
        });
      }
    },
    [rejectRecognition, toast, closeReject]
  );

  const selectableList = list.filter((m) => !isInsufficient(m));

  const isAllSelected =
    selectableList.length > 0 && selectableList.every((m) => selected.some((s) => s.id === m.id));

  const isAllSelectedIndeterminate =
    selectableList.length > 0 &&
    selectableList.some((m) => selected.some((s) => s.id === m.id)) &&
    !isAllSelected;

  const onToggleSelectAll = useCallback(() => {
    setSelected(isAllSelected ? [] : selectableList);
  }, [isAllSelected, selectableList]);

  const handleOnSelect = useCallback(
    (id: string) => {
      setSelected((prev) => {
        if (prev.some((item) => item.id === id)) {
          return prev.filter((item) => item.id !== id);
        }
        const found = list.find((item) => item.id === id);
        if (!found || isInsufficient(found)) return prev;
        return [...prev, found];
      });
    },
    [list]
  );

  const openApproveConfirm = useCallback(
    (items: RecognitionWithPot[]) => {
      setPendingItems(items);
      openApprove();
    },
    [openApprove]
  );

  const openRejectConfirm = useCallback(
    (items: RecognitionWithPot[]) => {
      setPendingItems(items);
      openReject();
    },
    [openReject]
  );

  const handleConfirmApprove = useCallback(() => {
    onApprove(pendingItems);
  }, [pendingItems, onApprove]);

  const handleConfirmReject = useCallback(
    (reason: string) => {
      onReject(pendingItems, reason);
    },
    [pendingItems, onReject]
  );

  const handleCloseApprove = useCallback(() => {
    closeApprove();
    setPendingItems([]);
  }, [closeApprove]);

  const handleCloseReject = useCallback(() => {
    closeReject();
    setPendingItems([]);
  }, [closeReject]);

  return (
    <>
      <Flex flexDir="column" justifyContent="flex-start" gap="16px" h="full">
        <Flex
          w="full"
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap="15px"
        >
          <Checkbox
            ml="20px"
            isIndeterminate={isAllSelectedIndeterminate}
            isChecked={isAllSelected}
            isDisabled={list.length === 0}
            onChange={onToggleSelectAll}
            variant="selectAll"
          >
            Select all
          </Checkbox>

          {potDropdown}
        </Flex>
        <Flex direction="column" gap="16px" flex="1">
          {list.length === 0 ? (
            <Box flex="1">
              <PageEmptyState {...ApprovalsTabEmptyPage[tab]} />
            </Box>
          ) : (
            list.map((item) => (
              <Box key={item.id}>
                <ApprovalCard
                  prideManagers={item.potManagers?.map((m) => m.individualName) || []}
                  prideName={item.groupWithPot?.name}
                  pot={item.groupWithPot?.pot}
                  key={item.id}
                  requestDate={item.submittedAt}
                  points={item.points}
                  pointsValue={item.points ? getPointsValue(item.points) : undefined}
                  isSelectable
                  isSelectDisabled={isInsufficient(item)}
                  isInsufficientBalance={isInsufficient(item)}
                  isSelected={selected.some((s) => s.id === item.id)}
                  onSelect={() => handleOnSelect(item.id)}
                  requester={individualFullName({
                    firstName: item.senderFirstName,
                    lastName: item.senderLastName,
                  } as Individual)}
                  recipient={individualFullName({
                    firstName: item.receiverFirstName,
                    lastName: item.receiverLastName,
                  } as Individual)}
                  onApprove={() => openApproveConfirm([item])}
                  onReject={() => openRejectConfirm([item])}
                  onOpenDetails={() => {
                    setSelectedRecognition(item);
                    openDetails();
                  }}
                  isEscalated={tab === 'escalations'}
                />
              </Box>
            ))
          )}
        </Flex>

        <Flex
          position="fixed"
          left={{ base: 0, lg: 240 }}
          right={0}
          bottom={0}
          justify="center"
          maxW="1440px"
          m="20px auto"
          px={{ base: '30px', md: '40px' }}
          zIndex={10}
          display={selected.length > 0 ? 'flex' : 'none'}
        >
          <BulkActionBar
            onDeselectAll={() => setSelected([])}
            selectedCount={selected.length}
            h="60px"
            cta={
              <Flex direction="row">
                <Button variant="delete" size="sm" onClick={() => openRejectConfirm(selected)}>
                  <Flex direction="column" gap="5px" align="center">
                    <CloseCircleOutline boxSize="20px" />
                    Decline
                  </Flex>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openApproveConfirm(selected)}
                  color="brand.white"
                  _hover={{ color: 'brand.white' }}
                >
                  <Flex direction="column" gap="5px" align="center">
                    <TickCircleOutline boxSize="20px" />
                    Approve
                  </Flex>
                </Button>
              </Flex>
            }
          />
        </Flex>
      </Flex>

      <ApproveRecognitionModal
        isOpen={isApproveOpen}
        onClose={handleCloseApprove}
        onConfirm={handleConfirmApprove}
        pendingItems={pendingItems}
        isEscalation={tab === 'escalations'}
      />

      <RejectRecognitionModal
        isOpen={isRejectOpen}
        onClose={handleCloseReject}
        onConfirm={handleConfirmReject}
        pendingItems={pendingItems}
        isEscalation={tab === 'escalations'}
      />

      <RecognitionDetailsModal
        isOpen={isDetailsOpen}
        isInsufficientBalance={!!selectedRecognition && isInsufficient(selectedRecognition)}
        onClose={closeDetails}
        onApprove={() => {
          if (!selectedRecognition) return;
          closeDetails();
          openApproveConfirm([selectedRecognition]);
        }}
        onReject={() => {
          if (!selectedRecognition) return;
          closeDetails();
          openRejectConfirm([selectedRecognition]);
        }}
        recognition={selectedRecognition}
      />
    </>
  );
}

export default RecognitionApprovalsList;
